import { useState, useEffect } from 'react';
import { supabase, handleSupabaseError } from '../lib/supabase';
import { Appointment } from '../types';
import { PredictionEngine } from '../lib/predictionEngine';

const getCurrentTenantId = async (): Promise<string | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return null;
  const { data } = await supabase
    .from('tenant_users')
    .select('tenant_id')
    .eq('auth_user_id', session.user.id)
    .maybeSingle();
  return data?.tenant_id ?? null;
};

export const useAppointments = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('appointments')
        .select(`
          *,
          patients (
            patient_id,
            age_group,
            email,
            phone,
            preferred_contact_method,
            email_consent,
            sms_consent
          )
        `)
        .order('appointment_date', { ascending: true });

      if (error) throw error;

      const formattedAppointments: Appointment[] = data.map(apt => ({
        id: apt.id,
        patientId: apt.patients?.patient_id || '',
        patientAgeGroup: apt.patients?.age_group || '',
        appointmentDate: apt.appointment_date,
        appointmentTime: apt.appointment_time,
        visitType: apt.visit_type,
        location: apt.location,
        department: apt.department,
        noShowProbability: apt.no_show_probability || 0,
        riskLevel: apt.risk_level,
        status: apt.status,
        remindersSent: apt.reminders_sent || 0,
        lastReminderSent: apt.last_reminder_sent || undefined
      }));

      setAppointments(formattedAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadData = async (file: File): Promise<boolean> => {
    try {
      setLoading(true);

      const tenantId = await getCurrentTenantId();
      if (!tenantId) throw new Error('No tenant assigned to current user');

      // Parse CSV file
      const text = await file.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const appointments = [];
      const patients = new Map();
      
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        if (values.length < headers.length) continue;
        
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index];
        });
        
        // Create or get patient
        if (!patients.has(row['Patient ID'])) {
          patients.set(row['Patient ID'], {
            patient_id: row['Patient ID'],
            age_group: row['Age Group'] || '25-35',
            location: row['Location'] || 'Main Hospital',
            historical_attendance: parseFloat(row['Historical Attendance']) || 80,
            email: row['Patient Email'] || `${row['Patient ID']}@demo.com`,
            phone: row['Patient Phone'] || '+1234567890',
            preferred_contact_method: row['Preferred Contact Method'] || 'both',
            email_consent: row['Email Consent'] !== 'false',
            sms_consent: row['SMS Consent'] !== 'false',
            tenant_id: tenantId
          });
        }
        
        // Calculate risk level based on historical attendance
        const attendance = parseFloat(row['Historical Attendance']) || 80;
        const probability = Math.max(0, Math.min(100, 100 - attendance + Math.random() * 20 - 10));
        let riskLevel: 'low' | 'medium' | 'high' = 'low';
        if (probability > 70) riskLevel = 'high';
        else if (probability > 40) riskLevel = 'medium';
        
        // Parse and format date from DD/MM/YYYY to YYYY-MM-DD
        const parseDate = (dateStr: string): string => {
          if (!dateStr) return new Date().toISOString().split('T')[0];
          
          // Handle DD/MM/YYYY format
          if (dateStr.includes('/')) {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              const day = parts[0].padStart(2, '0');
              const month = parts[1].padStart(2, '0');
              const year = parts[2];
              return `${year}-${month}-${day}`;
            }
          }
          
          // If already in YYYY-MM-DD format or other format, return as is
          return dateStr;
        };
        
        appointments.push({
          appointment_date: parseDate(row['Appointment Date']),
          appointment_time: row['Appointment Time'],
          visit_type: row['Visit Type'] || 'Consultation',
          location: row['Location'] || 'Main Hospital',
          department: row['Department'] || 'General Medicine',
          no_show_probability: Math.round(probability),
          risk_level: riskLevel,
          status: 'scheduled' as const,
          reminders_sent: 0,
          tenant_id: tenantId
        });
      }
      
      // Insert patients first
      const patientData = Array.from(patients.values());
      const { data: insertedPatients, error: patientsError } = await supabase
        .from('patients')
        .upsert(patientData, { onConflict: 'patient_id' })
        .select();
        
      if (patientsError) throw patientsError;
      
      // Create patient ID mapping
      const patientIdMap = new Map();
      insertedPatients?.forEach(p => {
        patientIdMap.set(p.patient_id, p.id);
      });
      
      // Add patient_id to appointments
      const appointmentsWithPatientIds = appointments.map((apt, index) => ({
        ...apt,
        patient_id: patientIdMap.get(Array.from(patients.keys())[index])
      }));
      
      // Insert appointments
      const { error: appointmentsError } = await supabase
        .from('appointments')
        .insert(appointmentsWithPatientIds);
        
      if (appointmentsError) throw appointmentsError;
      
      // Log the upload to upload_history
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await supabase.from('upload_history').insert({
          tenant_id: tenantId,
          uploaded_by: session.user.id,
          file_name: file.name,
          record_count: appointments.length
        });
      }

      // Refresh appointments list
      await fetchAppointments();
      return true;
    } catch (error) {
      console.error('Error uploading data:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const runPrediction = async (appointmentIds: string[]): Promise<boolean> => {
    try {
      // Get detailed appointment and patient data for predictions
      const { data: appointmentData, error: fetchError } = await supabase
        .from('appointments')
        .select(`
          *,
          patients (
            patient_id,
            age_group,
            historical_attendance,
            location
          )
        `)
        .in('id', appointmentIds);

      if (fetchError) throw fetchError;

      // Prepare data for Edge Function (includes all appointment and patient data)
      const appointmentsForPrediction = appointmentData.map(appointment => ({
        id: appointment.id,
        appointment_date: appointment.appointment_date,
        appointment_time: appointment.appointment_time,
        visit_type: appointment.visit_type,
        location: appointment.location,
        department: appointment.department,
        patients: appointment.patients
      }));

      // Call Supabase Edge Function with embedded PredictionEngine
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/predict-no-show`;
      const headers = {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ appointments: appointmentsForPrediction })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Prediction failed: ${errorData.error || response.statusText}`);
      }

      const { predictions } = await response.json();
      
      // Update appointments in database with predictions
      for (const prediction of predictions) {
        const { error } = await supabase
          .from('appointments')
          .update({
            no_show_probability: prediction.noShowProbability,
            risk_level: prediction.riskLevel
          })
          .eq('id', prediction.id);
          
        if (error) throw error;
      }
      
      // Refresh appointments list
      await fetchAppointments();
      return true;
    } catch (error) {
      console.error('Error running predictions:', error);
      return false;
    }
  };

  return { appointments, loading, uploadData, runPrediction, refreshAppointments: fetchAppointments };
};